import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

/** Keys that move the highlight through the options (cmdk's own list navigation). */
const LIST_NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'])

/**
 * cmdk's `value` while nothing is highlighted. Not empty on purpose: cmdk highlights the first
 * option by itself whenever its value is empty, and an option label can never be this.
 */
const NO_HIGHLIGHT = '\u0000'

type UseKeyboardFirstCommandListOptions = {
  /** Off: none of the keyboard-first behavior applies and focus stays on the trigger on open. */
  enabled: boolean
  /** Labels (cmdk item values) of the options currently shown, in order. Memoize it. */
  visibleLabels: string[]
  /** Closes the dropdown through the host's own open-change path. */
  close: () => void
  /** Picks the option with this label — Space on the focused list. Enter goes through cmdk. */
  pick: (label: string) => void
  /** Option highlighted when the list takes focus with nothing highlighted. Defaults to the first. */
  preferredLabel?: string
}

/**
 * Keyboard-first opening for a cmdk dropdown in a popover: the search field takes focus on open,
 * Tab moves focus onto the option list and Shift+Tab back, and the arrow keys move through the
 * options from either — instead of scrolling the surrounding panel. Enter (or Space on the list)
 * picks the highlighted option. Nothing is highlighted until the user moves through the list
 * (arrows, typing, hover, Tab onto it), so Enter on an untouched list just closes it, like a second
 * Enter on the trigger (WMPCM-11322). Tab off either end closes the list and returns focus to the
 * trigger, so Tab carries on from the field.
 */
export function useKeyboardFirstCommandList({
  enabled,
  visibleLabels,
  close,
  pick,
  preferredLabel,
}: UseKeyboardFirstCommandListOptions) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  // The highlighted option (cmdk's `value`, an option label), and whether the user has started
  // moving through the list since it opened.
  const [highlighted, setHighlighted] = useState('')
  const navigatedRef = useRef(false)

  /** Call when the dropdown opens: nothing is highlighted until the user moves. */
  function resetHighlight() {
    navigatedRef.current = false
    setHighlighted('')
  }

  /** Closes the list and hands focus back to the trigger, so Tab carries on from the field. */
  function closeToTrigger() {
    close()
    triggerRef.current?.focus()
  }

  // Typing filters the list: keep the highlight on a visible option (the first match), so Enter
  // picks what the user is looking at.
  useEffect(() => {
    if (!enabled || !navigatedRef.current) return

    if (!visibleLabels.includes(highlighted)) setHighlighted(visibleLabels[0] ?? '')
  }, [enabled, highlighted, visibleLabels])

  // Runs as the option list mounts (it renders inside the popover, after `open` flips) and
  // unmounts. cmdk's list renders `tabIndex={-1}` over any prop, so make it a Tab stop on the
  // element itself. Since it can then hold focus, also point assistive tech at its highlighted
  // option (the way cmdk does for the search field) — watched rather than read at render time,
  // because cmdk marks the highlighted option (`data-selected`) in its own row re-renders.
  const listObserverRef = useRef<MutationObserver | null>(null)
  const setListElement = useCallback(
    (list: HTMLDivElement | null) => {
      listRef.current = list
      listObserverRef.current?.disconnect()
      listObserverRef.current = null

      if (!list || !enabled) return

      list.tabIndex = 0

      const syncActiveDescendant = () => {
        const selectedId = list.querySelector<HTMLElement>('[cmdk-item][data-selected="true"]')?.id

        if (selectedId) {
          list.setAttribute('aria-activedescendant', selectedId)
        } else {
          list.removeAttribute('aria-activedescendant')
        }
      }

      syncActiveDescendant()
      listObserverRef.current = new MutationObserver(syncActiveDescendant)
      listObserverRef.current.observe(list, {
        subtree: true,
        childList: true,
        attributeFilter: ['data-selected'],
      })
    },
    [enabled]
  )

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (LIST_NAVIGATION_KEYS.has(event.key)) {
      navigatedRef.current = true
      return
    }

    // Nothing highlighted yet: Enter closes rather than silently picking the first option
    // (WMPCM-11322). Prevented, so cmdk's own Enter handling skips it.
    if (event.key === 'Enter' && !navigatedRef.current) {
      event.preventDefault()
      event.stopPropagation()
      closeToTrigger()

      return
    }

    // Shift+Tab off the start of the dropdown goes back to the field, like Tab off its end. The
    // list renders in a portal at the end of the page, so the browser's own Shift+Tab would land
    // on an unrelated element and the dropdown would close behind it.
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()
      closeToTrigger()
    }
  }

  function handleListFocus() {
    navigatedRef.current = true
    if (highlighted || visibleLabels.length === 0) return

    setHighlighted(
      preferredLabel && visibleLabels.includes(preferredLabel) ? preferredLabel : visibleLabels[0]
    )
  }

  function handleListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Space picks the highlighted option, like Enter (cmdk handles Enter but not Space). Prevented
    // so the list doesn't scroll.
    if (event.key === ' ') {
      event.preventDefault()
      if (highlighted) pick(highlighted)

      return
    }

    // Tab off the end of the list leaves the dropdown; Shift+Tab goes back to the search field.
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault()
      closeToTrigger()
    }
  }

  return {
    triggerRef,
    inputRef,
    listRef,
    setListElement,
    resetHighlight,
    /** For `PopoverContent`'s `onOpenAutoFocus`, after preventing Radix's default. */
    focusSearch: () => {
      if (enabled) inputRef.current?.focus()
    },
    commandProps: enabled
      ? {
          value: highlighted || NO_HIGHLIGHT,
          // cmdk proposes a highlight on its own when the list renders; only take it once the
          // user is actually moving through the list.
          onValueChange: (next: string) => {
            if (navigatedRef.current) setHighlighted(next)
          },
        }
      : {},
    /** Call from the search field's `onValueChange`: typing counts as moving through the list. */
    markSearchTyped: () => {
      if (enabled) navigatedRef.current = true
    },
    searchKeyDown: enabled ? handleSearchKeyDown : undefined,
    listProps: enabled
      ? {
          className:
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          onFocus: handleListFocus,
          onKeyDown: handleListKeyDown,
          // Before the hovered item's own handler proposes it as the highlight.
          onPointerMoveCapture: () => {
            navigatedRef.current = true
          },
        }
      : {},
  }
}
